import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 1,
  class: "form__submit btn btn--fullscreen",
  type: "submit"
}
const _hoisted_4 = {
  key: 2,
  class: "form__submit"
}
const _hoisted_5 = {
  class: "btn btn--fullscreen",
  type: "submit"
}

import {computed, ref} from "vue";
import {Status} from "@/validation";
import {min, optionalEmail, mustMatchPhoneNumberFormat, required} from "@/validation/rules";
import FormInput from "@/components/forms/FormInput.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormRadios from "@/components/forms/FormRadios.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import ProductBaseForm from "@/components/products/ProductBaseForm.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import FormTooltip from "@/components/forms/FormTooltip.vue";

import {useI18n} from "vue3-i18n";
import {Guid} from "@/types";
import {useRouter, useRoute} from "vue-router";
import {useActivityService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import {Activity, ActivityChapter, ActivityLocation} from "@/types/entities/activity";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import ActivityOccurrenceForm from "@/components/activities/ActivityOccurrenceForm.vue";

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityForm',
  props: {
    id: {}
  },
  async setup(__props: any) {

let __temp: any, __restore: any

const props = __props

const {t} = useI18n();

const router = useRouter();
const route = useRoute();
const activityService = useActivityService();

const isEditMode = props.id && Guid.isValid(props.id);
const isDuplicateMode = route.query.duplicateFromId && Guid.isValid(route.query.duplicateFromId as string);
const activity = ref<Activity>(
    isEditMode
      ? (
  ([__temp,__restore] = _withAsyncContext(() => activityService.getActivity(props.id))),
  __temp = await __temp,
  __restore(),
  __temp
)
      : isDuplicateMode
        ? (
  ([__temp,__restore] = _withAsyncContext(() => activityService.getActivity(route.query.duplicateFromId as string).then(a => ({ ...a, id: undefined, isPublished: false, activityOccurrences: [] })))),
  __temp = await __temp,
  __restore(),
  __temp
)
        : new Activity()
);
// Coerce a missing region to the empty value so the "Toutes les régions" option stays selected in edit mode.
activity.value.chapter = activity.value.chapter || "";
// An unpublished activity can never be private (hidden = published + private).
if (!activity.value.isPublished) activity.value.isPrivate = false;
const formInputs = ref<any[]>([])
const activityOccurenceForm = ref()
const inputValidationStatuses: any = {}

const locationOptions = [
  {name: ActivityLocation.OnSite, label: t("activities.form.location-on-site")},
  {name: ActivityLocation.Online, label: t("activities.form.location-online")},
];

const regionOptions = [
  {name: "", label: t("activities.form.region-all")},
  {name: ActivityChapter.Montreal, label: t("activities.form.region-montreal")},
  {name: ActivityChapter.Outaouais, label: t("activities.form.region-outaouais")},
  {name: ActivityChapter.Quebec, label: t("activities.form.region-quebec")},
  {name: ActivityChapter.Estrie, label: t("activities.form.region-estrie")},
  {name: ActivityChapter.Provincial, label: t("activities.form.region-provincial")},
];

const isOnline = computed(() => {
  if (!activity.value.location)
    return false;
  return activity.value.location == ActivityLocation.Online;
});

function onPublishedChange(value: boolean) {
  if (!value) {
    activity.value.isPrivate = false;
  }
}

async function onImageChange(event: any, type: string) {
  const file = event?.target?.files[0];
  if (type === "page")
    activity.value.pageImage = file as File;
  if (type === "pageEn")
    activity.value.pageImageEn = file as File;
  if (type === "participantGuideUrl")
    activity.value.participantGuideUrl = file as File;
  if (type === "activityPresentationUrl")
    activity.value.activityPresentationUrl = file as File;
}

async function onSavedImageChange(url: any, type: string) {
  if (type === "page") activity.value.savedPageImage = url
  if (type === "pageEn") activity.value.savedPageImageEn = url
}

async function onActivityOccurrencesChange(activityOccurrences: any) {
  activity.value.activityOccurrences = activityOccurrences;
}


function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref) && ref)
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  await activityOccurenceForm.value.validateForm()

  if (activity.value.activityOccurrences.length == 0) {
    notifyError(t('activityOccurrences.form.mustEnterAtLeastOneOccurrence'))
    return;
  }

  let validKeyNames = Object.keys(inputValidationStatuses).filter(x => x.length);
  if (Object.values(validKeyNames.map(key => inputValidationStatuses[key])).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  } else {
    await save();
  }
}

async function save() {
  let succeededOrNotResponse: SucceededOrNotResponse
  activity.value.activityOccurrences.forEach(occurrence => {
    occurrence.startDate = occurrence.startDate ? occurrence.startDate : "";
    occurrence.endDate = occurrence.endDate ? occurrence.endDate : "";
  })

  if (!activity.value.activityOccurrences.every(x => x.startDate?.length)) {
    notifyError(t('activities.form.saveError'))
    return;
  }

  let request: any = {
    ...activity.value,
    activityOccurrences: JSON.stringify(activity.value.activityOccurrences)
  }
  if (isEditMode) {
    succeededOrNotResponse = await activityService.updateActivity(request);
  } else {
    succeededOrNotResponse = await activityService.createActivity(request);
  }

  if (succeededOrNotResponse.succeeded) {
    notifySuccess(t('activities.form.saveSuccess'))
    setTimeout(() => {
      router.back();
    }, 1500);
  } else {
    displayErrors(succeededOrNotResponse)
    notifyError(errorsMessages.value.length == 1 ? errorsMessages.value[0] : t('activities.form.saveError'))
  }
}

async function deleteActivity() {
  if (!Guid.isValid(activity.value.id ?? "") || activity.value.id == null)
    return;

  let confirmDelete = confirm(t("activities.form.deleteConfirmation"));

  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await activityService.deleteActivity(activity.value.id);
  if (result?.succeeded) {
    notifySuccess(t('activities.form.deleteSuccess'))
    await router.push({name: "admin.children.activities.index"});
  } else {
    displayErrors(result as SucceededOrNotResponse)
    notifyError(errorsMessages.value.length == 1 ? errorsMessages.value[0] : t('activities.form.deleteError'))
  }
}

const errorsMessages = ref<string[]>([])

function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.activity', 'validation.activity.errorOccurred')
  } else
    errorsMessages.value = []
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "form",
    enctype: "multipart/form-data",
    novalidate: "",
    onSubmit: _withModifiers(handleSubmit, ["prevent"]),
    onKeydown: _cache[29] || (_cache[29] = _withKeys(_withModifiers(() => {}, ["prevent"]), ["enter"]))
  }, [
    (errorsMessages.value.length)
      ? (_openBlock(), _createBlock(FormErrorsList, {
          key: 0,
          errorsList: errorsMessages.value,
          title: _unref(t)('global.formErrorNotification')
        }, null, 8, ["errorsList", "title"]))
      : _createCommentVNode("", true),
    _createVNode(ProductBaseForm, {
      product: activity.value,
      title: _unref(t)('products.form.fieldset-informations-activity'),
      type: "Activity",
      onHandleValidation: handleValidation,
      onRegisterFormInputRef: addFormInputRef
    }, {
      image: _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormFiles, {
              ref: addFormInputRef,
              modelValue: activity.value.pageImage,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activity.value.pageImage) = $event)),
              "default-selected-files": activity.value.savedPageImage != null ? [activity.value.savedPageImage] : [],
              label: _unref(t)('products.form.image-page'),
              possiblesTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
              tooltip: _unref(t)(`products.form.image-page-tooltip`),
              "can-delete": "",
              name: "pageImage",
              onChange: _cache[1] || (_cache[1] = ($event: any) => (onImageChange($event, 'page'))),
              onValidated: handleValidation,
              onOnDefaultSelectedFilesChange: _cache[2] || (_cache[2] = ($event: any) => (onSavedImageChange($event, 'page')))
            }, null, 8, ["modelValue", "default-selected-files", "label", "tooltip"]),
            _createVNode(FormFiles, {
              ref: addFormInputRef,
              modelValue: activity.value.pageImageEn,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((activity.value.pageImageEn) = $event)),
              "default-selected-files": activity.value.savedPageImageEn != null ? [activity.value.savedPageImageEn] : [],
              label: _unref(t)('products.form.image-page-en'),
              possiblesTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
              tooltip: _unref(t)(`products.form.image-page-tooltip`),
              "can-delete": "",
              name: "pageImageEn",
              onChange: _cache[4] || (_cache[4] = ($event: any) => (onImageChange($event, 'pageEn'))),
              onValidated: handleValidation,
              onOnDefaultSelectedFilesChange: _cache[5] || (_cache[5] = ($event: any) => (onSavedImageChange($event, 'pageEn')))
            }, null, 8, ["modelValue", "default-selected-files", "label", "tooltip"])
          ]),
          _: 1
        })
      ]),
      "after-images": _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.imageOverlayTextFr,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((activity.value.imageOverlayTextFr) = $event)),
              label: _unref(t)('products.form.image-text-fr'),
              rules: [],
              name: "image-text-fr",
              type: "text",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.imageOverlayTextEn,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((activity.value.imageOverlayTextEn) = $event)),
              label: _unref(t)('products.form.image-text-en'),
              rules: [],
              name: "image-text-en",
              type: "text",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["product", "title"]),
    _createVNode(Fieldset, {
      title: _unref(t)('activities.form.fieldset-schedule')
    }, {
      default: _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.startsAt,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((activity.value.startsAt) = $event)),
              label: _unref(t)('activities.form.startsAt'),
              rules: [],
              name: "starts-at",
              type: "time",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.endsAt,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((activity.value.endsAt) = $event)),
              label: _unref(t)('activities.form.endsAt'),
              rules: [],
              name: "ends-at",
              type: "time",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.trainer,
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((activity.value.trainer) = $event)),
              label: _unref(t)('activities.form.trainer'),
              rules: [],
              name: "trainer",
              type: "text",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.trainerEmail,
              "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((activity.value.trainerEmail) = $event)),
              label: _unref(t)('activities.form.trainerEmail'),
              rules: [_unref(optionalEmail)],
              name: "trainerEmail",
              type: "email",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "rules"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(ActivityOccurrenceForm, {
      ref_key: "activityOccurenceForm",
      ref: activityOccurenceForm,
      "model-value": activity.value.activityOccurrences,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((activity.value.activityOccurrences) = $event)),
      "activity-id": props.id,
      onValidated: handleValidation,
      onUpdatedActivityOccurrences: onActivityOccurrencesChange
    }, null, 8, ["model-value", "activity-id"]),
    _createVNode(Fieldset, {
      title: _unref(t)('activities.form.fieldset-location')
    }, {
      default: _withCtx(() => [
        _createVNode(FormRadios, {
          ref: addFormInputRef,
          modelValue: activity.value.location,
          "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((activity.value.location) = $event)),
          isRequired: true,
          label: _unref(t)('activities.form.location'),
          options: locationOptions,
          name: "location",
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"]),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormSelect, {
              modelValue: activity.value.chapter,
              "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((activity.value.chapter) = $event)),
              label: _unref(t)('activities.form.region'),
              options: regionOptions,
              rules: [],
              name: "chapter",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormRow, { "with-three-columns": "" }, {
          default: _withCtx(() => [
            (!isOnline.value)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 0,
                  ref: addFormInputRef,
                  modelValue: activity.value.locationAddress,
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((activity.value.locationAddress) = $event)),
                  label: _unref(t)('activities.form.address'),
                  rules: [],
                  name: "location-address",
                  type: "text",
                  onValidated: handleValidation
                }, null, 8, ["modelValue", "label"]))
              : _createCommentVNode("", true),
            (isOnline.value)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 1,
                  ref: addFormInputRef,
                  modelValue: activity.value.onlineUrl,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((activity.value.onlineUrl) = $event)),
                  label: _unref(t)('activities.form.url'),
                  rules: [],
                  name: "online-url",
                  type: "text",
                  onValidated: handleValidation
                }, null, 8, ["modelValue", "label"]))
              : _createCommentVNode("", true),
            (isOnline.value)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 2,
                  ref: addFormInputRef,
                  modelValue: activity.value.onlinePhoneNumber,
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((activity.value.onlinePhoneNumber) = $event)),
                  label: _unref(t)('global.phoneNumber'),
                  rules: [_unref(required), _unref(mustMatchPhoneNumberFormat)],
                  name: "online-phone-number",
                  type: "tel",
                  onValidated: handleValidation
                }, null, 8, ["modelValue", "label", "rules"]))
              : _createCommentVNode("", true),
            (isOnline.value)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 3,
                  ref: addFormInputRef,
                  modelValue: activity.value.onlineWebinarNumber,
                  "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((activity.value.onlineWebinarNumber) = $event)),
                  label: _unref(t)('activities.form.webinarNumber'),
                  rules: [],
                  name: "online-webinar-number",
                  type: "text",
                  onValidated: handleValidation
                }, null, 8, ["modelValue", "label"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Fieldset, {
      title: _unref(t)('activities.form.fieldset-participants')
    }, {
      default: _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.maxParticipants,
              "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((activity.value.maxParticipants) = $event)),
              label: _unref(t)('activities.form.maxParticipants'),
              rules: [_unref(required), _unref(min)(1)],
              name: "maxParticipants",
              type: "number",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "rules"])
          ]),
          _: 1
        }),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.surveyUrl,
              "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((activity.value.surveyUrl) = $event)),
              label: _unref(t)('activityOccurrences.form.surveyUrl'),
              rules: [],
              name: "surveyUrl",
              type: "text",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormCheckbox, {
              modelValue: activity.value.isPrivate,
              "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((activity.value.isPrivate) = $event)),
              label: _unref(t)('activities.form.private'),
              name: "isPrivate",
              disabled: !activity.value.isPublished,
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "disabled"]),
            _createVNode(FormCheckbox, {
              modelValue: activity.value.isPublished,
              "onUpdate:modelValue": [
                _cache[22] || (_cache[22] = ($event: any) => ((activity.value.isPublished) = $event)),
                onPublishedChange
              ],
              label: _unref(t)('activities.form.publish'),
              name: "isPublished",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormCheckbox, {
              modelValue: activity.value.isProfessionalsOnly,
              "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((activity.value.isProfessionalsOnly) = $event)),
              label: _unref(t)('activities.form.professionalsOnly'),
              name: "isProfessionalsOnly",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormTooltip, null, {
          default: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(_unref(t)('activities.form.hiddenActivityInfo')), 1),
            _createElementVNode("p", null, _toDisplayString(_unref(t)('activities.form.professionalsOnlyInfo')), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Fieldset, {
      title: _unref(t)('activities.form.fieldset-documents')
    }, {
      default: _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: addFormInputRef,
              modelValue: activity.value.activityRecordingUrl,
              "onUpdate:modelValue": _cache[24] || (_cache[24] = ($event: any) => ((activity.value.activityRecordingUrl) = $event)),
              label: _unref(t)('activityOccurrences.form.activityRecordingUrl'),
              rules: [],
              name: "activityRecordingUrl",
              type: "text",
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormFiles, {
              modelValue: activity.value.participantGuideUrl,
              "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((activity.value.participantGuideUrl) = $event)),
              "default-selected-files": activity.value.participantGuideUrl != undefined ? [activity.value.participantGuideUrl] : [],
              label: _unref(t)('activityOccurrences.form.participantGuideUrl'),
              name: "participantGuideUrl",
              onChange: _cache[26] || (_cache[26] = ($event: any) => (onImageChange($event, 'participantGuideUrl'))),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "default-selected-files", "label"]),
            _createVNode(FormFiles, {
              modelValue: activity.value.activityPresentationUrl,
              "onUpdate:modelValue": _cache[27] || (_cache[27] = ($event: any) => ((activity.value.activityPresentationUrl) = $event)),
              "default-selected-files": activity.value.activityPresentationUrl != undefined ? [activity.value.activityPresentationUrl] : [],
              label: _unref(t)('activityOccurrences.form.activityPresentationUrl'),
              name: "activityPresentationUrl",
              onChange: _cache[28] || (_cache[28] = ($event: any) => (onImageChange($event, 'activityPresentationUrl'))),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "default-selected-files", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormTooltip, null, {
          default: _withCtx(() => [
            _createElementVNode("p", {
              innerHTML: _unref(t)(`global.uploadZippedFolder-tooltip`)
            }, null, 8, _hoisted_2)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"]),
    (!_unref(isEditMode))
      ? (_openBlock(), _createElementBlock("button", _hoisted_3, _toDisplayString(_unref(t)("global.add")), 1))
      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("button", _hoisted_5, _toDisplayString(_unref(t)("global.update")), 1),
          _createElementVNode("button", {
            class: "btn btn--red btn--fullscreen",
            type: "button",
            onClick: deleteActivity
          }, _toDisplayString(_unref(t)("global.delete")), 1)
        ]))
  ], 40, _hoisted_1))
}
}

})